/**
 * Bulk AI Naming Modal
 * Issue #363: Post-analysis bulk AI naming for interactions
 *
 * Shows after analysis completes to offer AI naming for new interactions.
 */

import { useState, useCallback, useRef } from 'react';
import type { E2EFlowPath } from '@veodiagram/shared';
import type { NamingContext } from '../../hooks/useInteractionNaming';
import { apiFetch } from '../../services/api';

/**
 * Infer screen/page name from component file path
 */
function inferScreenName(filePath: string): string | undefined {
  if (!filePath) return undefined;

  const normalizedPath = filePath.replace(/\\/g, '/');

  // Extract from pages directory
  const pagesMatch = normalizedPath.match(/\/pages\/([^/]+)/);
  if (pagesMatch) {
    return pagesMatch[1].replace(/Page\.tsx$/, '').replace(/\.tsx$/, '');
  }

  // Extract from views directory
  const viewsMatch = normalizedPath.match(/\/views\/([^/]+)/);
  if (viewsMatch) {
    return viewsMatch[1].replace(/View\.tsx$/, '').replace(/\.tsx$/, '');
  }

  // Extract from screens directory
  const screensMatch = normalizedPath.match(/\/screens\/([^/]+)/);
  if (screensMatch) {
    return screensMatch[1].replace(/Screen\.tsx$/, '').replace(/\.tsx$/, '');
  }

  // Check for shared/layout components
  if (/\/(layout|common|shared|ui)\//i.test(normalizedPath)) {
    return 'Shared / Layout';
  }

  // Extract component name as fallback
  const componentMatch = normalizedPath.match(/\/([^/]+)\.tsx$/);
  if (componentMatch) {
    return componentMatch[1].replace(/Component$/, '');
  }

  return undefined;
}

interface BulkAINamingModalProps {
  projectId: string;
  interactions: E2EFlowPath[];
  onComplete: (results: BulkNamingResult[]) => void;
  onSkip: () => void;
  onClose: () => void;
}

export interface BulkNamingResult {
  interactionId: string;
  originalName: string;
  aiSuggestedName: string;
  confidence: number;
  userAction: 'accepted' | 'rejected' | 'edited' | 'pending';
  finalName: string;
  source: 'ai' | 'pattern';  // Indicates whether AI or pattern-based fallback was used
  reasoning?: string;  // AI reasoning for the suggested name
}

type ModalPhase = 'prompt' | 'generating' | 'review' | 'applying';

// Issue #400: Apply progress tracking
interface ApplyProgress {
  current: number;
  total: number;
  succeeded: number;
  failed: number;
}

interface ApplyResult {
  total: number;
  succeeded: number;
  failed: number;
  failedItems: string[];
}

export function BulkAINamingModal({
  projectId,
  interactions,
  onComplete,
  onSkip,
  onClose,
}: BulkAINamingModalProps) {
  const [phase, setPhase] = useState<ModalPhase>('prompt');
  const [results, setResults] = useState<BulkNamingResult[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: interactions.length });
  const [autoApply, setAutoApply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);

  // Issue #400: Apply progress and result tracking
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ current: 0, total: 0, succeeded: 0, failed: 0 });
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);

  // Check AI status before generating
  const checkAIAvailability = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiFetch(`/projects/${projectId}/ai-status`, {
        credentials: 'include',
      });
      if (response.ok) {
        const status = await response.json();
        return status.available;
      }
      return false;
    } catch {
      return false;
    }
  }, [projectId]);

  // Generate AI names for all interactions
  const generateNames = useCallback(async () => {
    setError(null);

    // Check AI availability first
    const aiAvailable = await checkAIAvailability();
    if (!aiAvailable) {
      setError('AI service unavailable. Please add your API key in Settings to enable AI naming.');
      return;
    }

    setPhase('generating');
    abortRef.current = false;

    const batchSize = 3;
    const newResults: BulkNamingResult[] = [];

    for (let i = 0; i < interactions.length; i += batchSize) {
      if (abortRef.current) break;

      const batch = interactions.slice(i, i + batchSize);

      // Process batch
      const batchPromises = batch.map(async (flow) => {
        const userActionStep = flow.steps.find(s => s.type === 'user-action');
        const firstStep = userActionStep || flow.steps[0];

        // Extract code snippet from evidence if available
        const codeSnippet = firstStep?.evidence?.[0]?.snippet || '';

        // Infer screen name from component file path
        const componentFile = firstStep?.sourceFile || '';
        const screenName = inferScreenName(componentFile);

        // Extract API calls from flow steps
        const apiCalls = flow.steps
          .filter(s => s.type === 'api-call' || s.type === 'api-route')
          .map(s => s.name);

        // Build flow steps summary
        const flowSteps = flow.steps.map(s => s.label || s.name);

        const context: NamingContext = {
          handlerFunction: firstStep?.name || flow.triggerName,
          triggerElement: flow.triggerName,
          triggerType: flow.triggerType,
          componentFile,
          stateChanges: flow.steps.filter(s => s.type === 'state-change').map(s => s.name),
          // Enhanced context
          codeSnippet,
          screenName,
          interactionCategory: flow.interactionCategory,
          flowDescription: flow.description,
          apiCalls: apiCalls.length > 0 ? apiCalls : undefined,
          touchedTables: flow.touchedTables?.length > 0 ? flow.touchedTables : undefined,
          touchedServices: flow.touchedServices?.length > 0 ? flow.touchedServices : undefined,
          isCompleteFlow: flow.complete,
          flowSteps: flowSteps.length > 1 ? flowSteps : undefined,
        };

        try {
          const response = await fetch(
            `/api/projects/${projectId}/interactions/ai-name`,
            {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(context),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.code === 'AI_UNAVAILABLE') {
              throw new Error('AI_UNAVAILABLE');
            }
            throw new Error('Failed to generate name');
          }

          const data = await response.json();
          return {
            interactionId: flow.id,
            originalName: flow.name,
            aiSuggestedName: data.suggestedName,
            confidence: data.confidence,
            userAction: 'pending' as const,
            finalName: data.suggestedName,
            source: (data.source || 'ai') as 'ai' | 'pattern',
            reasoning: data.reasoning,
          };
        } catch (err) {
          // Fallback to pattern-based name
          const errMessage = err instanceof Error ? err.message : '';
          if (errMessage === 'AI_UNAVAILABLE') {
            setError('AI service unavailable. Please add your API key in Settings.');
          }
          return {
            interactionId: flow.id,
            originalName: flow.name,
            aiSuggestedName: flow.name,
            confidence: 0.4,
            userAction: 'pending' as const,
            finalName: flow.name,
            source: 'pattern' as const,
          };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      newResults.push(...batchResults);
      setResults([...newResults]);
      setProgress({ current: newResults.length, total: interactions.length });

      // Small delay between batches
      if (i + batchSize < interactions.length && !abortRef.current) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    if (!abortRef.current) {
      setPhase('review');
    }
  }, [interactions, projectId, checkAIAvailability]);

  // Handle cancel during generation
  const handleCancel = useCallback(() => {
    if (phase === 'generating') {
      abortRef.current = true;
      setPhase('prompt');
      setResults([]);
      setProgress({ current: 0, total: interactions.length });
    } else {
      onClose();
    }
  }, [phase, interactions.length, onClose]);

  // Handle action on a single result
  const handleResultAction = useCallback((id: string, action: 'accepted' | 'rejected', editedName?: string) => {
    setResults(prev => prev.map(r =>
      r.interactionId === id
        ? {
            ...r,
            userAction: editedName ? 'edited' : action,
            finalName: editedName || (action === 'accepted' ? r.aiSuggestedName : r.originalName),
          }
        : r
    ));
  }, []);

  // Accept all pending
  const handleAcceptAll = useCallback(() => {
    setResults(prev => prev.map(r =>
      r.userAction === 'pending' ? { ...r, userAction: 'accepted', finalName: r.aiSuggestedName } : r
    ));
  }, []);

  // Reject all pending
  const handleRejectAll = useCallback(() => {
    setResults(prev => prev.map(r =>
      r.userAction === 'pending' ? { ...r, userAction: 'rejected', finalName: r.originalName } : r
    ));
  }, []);

  // Issue #400: Submit feedback with retry logic
  const submitFeedbackWithRetry = useCallback(async (
    result: BulkNamingResult,
    feedbackType: 'good' | 'bad' | 'edited',
    maxRetries = 3
  ): Promise<boolean> => {
    const url = `/api/projects/${projectId}/interactions/name-feedback?${new URLSearchParams({ interactionId: result.interactionId, originalName: result.originalName })}`;
    const body = JSON.stringify({
      aiSuggestedName: result.aiSuggestedName,
      aiConfidence: result.confidence,
      feedbackType,
      userProvidedName: feedbackType === 'edited' ? result.finalName : undefined,
      reasoning: result.reasoning,
    });

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body,
        });

        if (response.ok) {
          return true;
        }

        // If rate limited (429), wait with exponential backoff and retry
        if (response.status === 429) {
          const backoffMs = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          console.log(`Rate limited, retrying in ${backoffMs}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, backoffMs));
          continue;
        }

        // For other errors, don't retry
        console.error(`Failed to submit feedback: ${response.status}`);
        return false;
      } catch (error) {
        console.error('Network error submitting feedback:', error);
        if (attempt < maxRetries - 1) {
          const backoffMs = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      }
    }
    return false;
  }, [projectId]);

  // Issue #400: Apply accepted/edited results with batching, progress, and retry
  const handleApply = useCallback(async () => {
    const toApply = results.filter(r => r.userAction === 'accepted' || r.userAction === 'edited');
    const rejected = results.filter(r => r.userAction === 'rejected');
    const allToProcess = [
      ...toApply.map(r => ({ result: r, feedbackType: r.userAction === 'edited' ? 'edited' as const : 'good' as const })),
      ...rejected.map(r => ({ result: r, feedbackType: 'bad' as const })),
    ];

    const total = allToProcess.length;
    if (total === 0) {
      onComplete(results);
      return;
    }

    // Switch to applying phase
    setPhase('applying');
    setApplyProgress({ current: 0, total, succeeded: 0, failed: 0 });
    setApplyResult(null);

    const BATCH_SIZE = 8;
    const BATCH_DELAY_MS = 1000;
    let succeeded = 0;
    let failed = 0;
    const failedItems: string[] = [];

    // Process in batches to respect rate limits
    for (let i = 0; i < allToProcess.length; i += BATCH_SIZE) {
      if (abortRef.current) break;

      const batch = allToProcess.slice(i, i + BATCH_SIZE);

      // Process batch concurrently
      const batchResults = await Promise.all(
        batch.map(async ({ result, feedbackType }) => {
          const success = await submitFeedbackWithRetry(result, feedbackType);
          return { result, success };
        })
      );

      // Update counts
      for (const { result, success } of batchResults) {
        if (success) {
          succeeded++;
        } else {
          failed++;
          failedItems.push(result.finalName || result.originalName);
        }
      }

      // Update progress
      setApplyProgress({
        current: Math.min(i + BATCH_SIZE, total),
        total,
        succeeded,
        failed,
      });

      // Delay between batches to respect rate limits
      if (i + BATCH_SIZE < allToProcess.length && !abortRef.current) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }

    // Set final result for summary display
    const finalResult: ApplyResult = { total, succeeded, failed, failedItems };
    setApplyResult(finalResult);

    // Auto-close after short delay if all succeeded, otherwise show summary
    if (failed === 0) {
      setTimeout(() => {
        onComplete(results);
      }, 1500);
    }
  }, [results, submitFeedbackWithRetry, onComplete]);

  // Count results by status
  const counts = {
    accepted: results.filter(r => r.userAction === 'accepted').length,
    rejected: results.filter(r => r.userAction === 'rejected').length,
    edited: results.filter(r => r.userAction === 'edited').length,
    pending: results.filter(r => r.userAction === 'pending').length,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🤖</span>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              {phase === 'prompt' && 'AI Naming Suggestions'}
              {phase === 'generating' && 'Generating AI Names...'}
              {phase === 'review' && 'Review AI Suggestions'}
              {phase === 'applying' && 'Applying Names...'}
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          {phase === 'prompt' && (
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-400">
                Found <span className="font-semibold text-slate-800 dark:text-slate-200">{interactions.length}</span> new interactions.
                Would you like AI to suggest names?
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Current names are pattern-based (e.g., "handleExportPNG" &rarr; "Export PNG").
                AI can generate more descriptive names based on context.
              </p>
              <label className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoApply}
                  onChange={(e) => setAutoApply(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Apply AI naming to future analyses automatically
                </span>
              </label>
            </div>
          )}

          {phase === 'generating' && (
            <div className="space-y-4">
              {/* Progress bar */}
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div
                  className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-center text-sm text-slate-600 dark:text-slate-400">
                {progress.current} / {progress.total}
              </p>

              {/* Results so far */}
              <div className="space-y-2 max-h-60 overflow-auto">
                {results.map((result, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-green-500">✓</span>
                    <span className="text-slate-500 dark:text-slate-400 font-mono text-xs">
                      {result.originalName}
                    </span>
                    <span className="text-slate-400">&rarr;</span>
                    <span className="text-slate-700 dark:text-slate-300">
                      "{result.aiSuggestedName}"
                    </span>
                  </div>
                ))}
                {progress.current < progress.total && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Processing: {interactions[progress.current]?.triggerName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {phase === 'review' && (
            <div className="space-y-3 max-h-96 overflow-auto">
              {results.map((result) => (
                <ReviewItem
                  key={result.interactionId}
                  result={result}
                  onAction={handleResultAction}
                />
              ))}
            </div>
          )}

          {/* Issue #400: Applying phase with progress bar and summary */}
          {phase === 'applying' && (
            <div className="space-y-4">
              {/* Progress bar */}
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${applyProgress.total > 0 ? (applyProgress.current / applyProgress.total) * 100 : 0}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">
                  Applying: {applyProgress.current} / {applyProgress.total}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-green-600 dark:text-green-400">
                    ✓ {applyProgress.succeeded} saved
                  </span>
                  {applyProgress.failed > 0 && (
                    <span className="text-red-600 dark:text-red-400">
                      ✗ {applyProgress.failed} failed
                    </span>
                  )}
                </div>
              </div>

              {/* Status indicator */}
              {!applyResult && (
                <div className="flex items-center justify-center gap-2 py-4 text-slate-500 dark:text-slate-400">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Saving names to server...</span>
                </div>
              )}

              {/* Summary result */}
              {applyResult && (
                <div className={`p-4 rounded-lg border ${
                  applyResult.failed === 0
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                }`}>
                  {applyResult.failed === 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">✅</span>
                      <div>
                        <p className="font-medium text-green-700 dark:text-green-300">
                          All {applyResult.succeeded} names applied successfully!
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          Closing automatically...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">⚠️</span>
                        <div>
                          <p className="font-medium text-amber-700 dark:text-amber-300">
                            {applyResult.succeeded} of {applyResult.total} names applied
                          </p>
                          <p className="text-sm text-amber-600 dark:text-amber-400">
                            {applyResult.failed} failed due to rate limiting or errors
                          </p>
                        </div>
                      </div>
                      {applyResult.failedItems.length > 0 && (
                        <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                          <p className="font-medium">Failed items:</p>
                          <ul className="mt-1 list-disc list-inside max-h-20 overflow-auto">
                            {applyResult.failedItems.slice(0, 5).map((name, i) => (
                              <li key={i}>{name}</li>
                            ))}
                            {applyResult.failedItems.length > 5 && (
                              <li>...and {applyResult.failedItems.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
          {phase === 'prompt' && (
            <>
              <button
                onClick={onSkip}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
              >
                Skip - Use Pattern Names
              </button>
              <button
                onClick={generateNames}
                className="px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center gap-2"
              >
                🤖 Generate AI Names
              </button>
            </>
          )}

          {phase === 'generating' && (
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            >
              Cancel
            </button>
          )}

          {phase === 'review' && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="text-green-500">{counts.accepted + counts.edited} accepted</span>
                <span>|</span>
                <span className="text-red-500">{counts.rejected} rejected</span>
                <span>|</span>
                <span>{counts.pending} pending</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAcceptAll}
                  className="px-3 py-1.5 text-xs text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded hover:bg-green-50 dark:hover:bg-green-900/50"
                >
                  Accept All
                </button>
                <button
                  onClick={handleRejectAll}
                  className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/50"
                >
                  Reject All
                </button>
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={counts.accepted + counts.edited === 0}
                  className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply {counts.accepted + counts.edited} Names
                </button>
              </div>
            </>
          )}

          {/* Issue #400: Applying phase footer */}
          {phase === 'applying' && (
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {!applyResult ? (
                  <span>Processing in batches to avoid rate limiting...</span>
                ) : applyResult.failed === 0 ? (
                  <span className="text-green-600 dark:text-green-400">Complete!</span>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">
                    Completed with {applyResult.failed} failures
                  </span>
                )}
              </div>
              {applyResult && (
                <button
                  onClick={() => onComplete(results)}
                  className={`px-4 py-2 text-sm rounded-lg ${
                    applyResult.failed === 0
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-amber-500 text-white hover:bg-amber-600'
                  }`}
                >
                  {applyResult.failed === 0 ? 'Done' : 'Close Anyway'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual review item component
function ReviewItem({
  result,
  onAction,
}: {
  result: BulkNamingResult;
  onAction: (id: string, action: 'accepted' | 'rejected', editedName?: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(result.aiSuggestedName);

  const handleSaveEdit = () => {
    onAction(result.interactionId, 'accepted', editValue);
    setIsEditing(false);
  };

  const actionStyles = {
    pending: 'border-slate-200 dark:border-slate-700',
    accepted: 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20',
    rejected: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 opacity-60',
    edited: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20',
  };

  return (
    <div className={`p-3 rounded-lg border ${actionStyles[result.userAction]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400 font-mono truncate">
            {result.originalName}
          </p>
          {isEditing ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1 px-2 py-1 text-sm border border-blue-400 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                autoFocus
              />
              <button
                onClick={handleSaveEdit}
                className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="mt-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  "{result.finalName}"
                </span>
                <span className="text-xs text-indigo-500">
                  {Math.round(result.confidence * 100)}%
                </span>
                {/* Source badge */}
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  result.source === 'ai'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                }`}>
                  {result.source === 'ai' ? '🤖 AI' : '📝 Pattern'}
                </span>
              </div>
              {/* Display AI reasoning */}
              {result.reasoning && (
                <p className="mt-1 text-[10px] text-indigo-600 dark:text-indigo-400 italic">
                  💡 {result.reasoning}
                </p>
              )}
            </div>
          )}
        </div>

        {!isEditing && result.userAction === 'pending' && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onAction(result.interactionId, 'accepted')}
              className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50 rounded"
              title="Accept"
            >
              👍
            </button>
            <button
              onClick={() => onAction(result.interactionId, 'rejected')}
              className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/50 rounded"
              title="Reject"
            >
              👎
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
              title="Edit"
            >
              ✏️
            </button>
          </div>
        )}

        {!isEditing && result.userAction !== 'pending' && (
          <span className={`text-xs px-2 py-0.5 rounded ${
            result.userAction === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
            result.userAction === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
            'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
          }`}>
            {result.userAction === 'accepted' ? 'Accepted' :
             result.userAction === 'rejected' ? 'Rejected' : 'Edited'}
          </span>
        )}
      </div>
    </div>
  );
}

export default BulkAINamingModal;
